
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLeagueDropdownOpen, setIsLeagueDropdownOpen] = useState(false);
  const [isMobileLeagueOpen, setIsMobileLeagueOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLeagueDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const goToHome = () => {
    navigate('/');
    setIsMenuOpen(false);
  };

  const goToPage = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsLeagueDropdownOpen(false);
    setIsMobileLeagueOpen(false);
  };

  const isLeaguePage = location.pathname === '/winter-league' || location.pathname === '/spring-league-registration';

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          <button 
            onClick={goToHome}
            className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-12 h-12 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/538ca75b02212864d9ecfdf82df77bb0/c894575bb6e66422299273d3fa52e9c0.png" 
                alt="Great Golf On The Go Logo" 
                className="w-12 h-12 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-green-800">Great Golf On The Go</h1>
              <p className="text-sm text-green-600">Mobile Golf Simulator</p>
            </div>
          </button>

          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-green-600 font-medium cursor-pointer whitespace-nowrap"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-green-600 font-medium cursor-pointer whitespace-nowrap"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('gallery')}
              className="text-gray-700 hover:text-green-600 font-medium cursor-pointer whitespace-nowrap"
            >
              Gallery
            </button>

            {/* Golf Leagues Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsLeagueDropdownOpen(!isLeagueDropdownOpen)}
                className={`flex items-center space-x-1 font-medium cursor-pointer whitespace-nowrap transition-colors ${isLeaguePage ? 'text-green-600' : 'text-gray-700 hover:text-green-600'}`}
              >
                <span>Golf Leagues</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isLeagueDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isLeagueDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-fade-in">
                  <a 
                    href="/winter-league"
                    onClick={(e) => { e.preventDefault(); goToPage('/winter-league'); }}
                    className={`flex items-center px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${location.pathname === '/winter-league' ? 'text-green-600 bg-green-50' : 'text-gray-700 hover:text-green-600 hover:bg-green-50/60'}`}
                  >
                    <i className="ri-snowy-line mr-3 text-base w-5 h-5 flex items-center justify-center"></i>
                    Winter League
                  </a>
                  <a 
                    href="/spring-league-registration"
                    onClick={(e) => { e.preventDefault(); goToPage('/spring-league-registration'); }}
                    className={`flex items-center px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${location.pathname === '/spring-league-registration' ? 'text-green-600 bg-green-50' : 'text-gray-700 hover:text-green-600 hover:bg-green-50/60'}`}
                  >
                    <i className="ri-sun-line mr-3 text-base w-5 h-5 flex items-center justify-center"></i>
                    Spring League
                  </a>
                </div>
              )}
            </div>

            <button 
              onClick={() => scrollToSection('booking')}
              className="text-gray-700 hover:text-green-600 font-medium cursor-pointer whitespace-nowrap"
            >
              Book Now
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="text-gray-700 hover:text-green-600 font-medium cursor-pointer whitespace-nowrap"
            >
              FAQ
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-green-600 font-medium cursor-pointer whitespace-nowrap"
            >
              Contact
            </button>
          </nav>

          <button 
            className="md:hidden text-gray-700 cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="flex flex-col space-y-2">
              <button 
                onClick={() => scrollToSection('home')}
                className="text-gray-700 hover:text-green-600 font-medium py-2 text-left cursor-pointer"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-green-600 font-medium py-2 text-left cursor-pointer"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('gallery')}
                className="text-gray-700 hover:text-green-600 font-medium py-2 text-left cursor-pointer"
              >
                Gallery
              </button>

              {/* Mobile Golf Leagues */}
              <div>
                <button 
                  onClick={() => setIsMobileLeagueOpen(!isMobileLeagueOpen)}
                  className={`flex items-center justify-between w-full font-medium py-2 text-left cursor-pointer ${isLeaguePage ? 'text-green-600' : 'text-gray-700 hover:text-green-600'}`}
                >
                  <span>Golf Leagues</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isMobileLeagueOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isMobileLeagueOpen && (
                  <div className="pl-4 flex flex-col space-y-1 mt-1">
                    <a 
                      href="/winter-league"
                      onClick={(e) => { e.preventDefault(); goToPage('/winter-league'); }}
                      className={`flex items-center py-2 text-sm font-medium cursor-pointer ${location.pathname === '/winter-league' ? 'text-green-600' : 'text-gray-600 hover:text-green-600'}`}
                    >
                      <i className="ri-snowy-line mr-2 text-base w-4 h-4 flex items-center justify-center"></i>
                      Winter League
                    </a>
                    <a 
                      href="/spring-league-registration"
                      onClick={(e) => { e.preventDefault(); goToPage('/spring-league-registration'); }}
                      className={`flex items-center py-2 text-sm font-medium cursor-pointer ${location.pathname === '/spring-league-registration' ? 'text-green-600' : 'text-gray-600 hover:text-green-600'}`}
                    >
                      <i className="ri-sun-line mr-2 text-base w-4 h-4 flex items-center justify-center"></i>
                      Spring League
                    </a>
                  </div>
                )}
              </div>

              <button 
                onClick={() => scrollToSection('booking')}
                className="text-gray-700 hover:text-green-600 font-medium py-2 text-left cursor-pointer"
              >
                Book Now
              </button>
              <button 
                onClick={() => scrollToSection('faq')}
                className="text-gray-700 hover:text-green-600 font-medium py-2 text-left cursor-pointer"
              >
                FAQ
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-gray-700 hover:text-green-600 font-medium py-2 text-left cursor-pointer"
              >
                Contact
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
